import { collection } from '../../infra/collection'
import { RoomDto } from '../dtos/rooms'
import { Room } from './Room'
import { GeneralError } from 'fejl'
import { PrimeMemberByPublicId } from '../../iam/MemberStore'
import { PrimeConversation } from '../ConversationStore'
import { Id, PrimeProps } from '../../types/definitions'
import Conversation from '../models/Conversation'
import { RoomsAPI } from './API'
import {
  mapConnectionDescriptorFromDto,
  RoomConnectionDescriptor,
} from './ConnectionDescriptor'
import { filterNonNil } from '../../utils/rx-util'
import { Observable, concatMap, share } from 'rxjs'
import { MessageEventsDTO } from '../dtos/messages'
import { MessagingAPI } from '../API'
import { RoomEvent } from './room-event'

/**
 * Prime Room callback.
 */
export type PrimeRoom = (props: PrimeProps<RoomDto, 'id'>) => Room

export default class RoomStore {
  /**
   * Room events.
   */
  readonly events$: Observable<RoomEvent>

  /**
   * Rooms collection
   */
  rooms = collection<RoomDto, Room>({
    idAttribute: 'id',
    create: (attrs) => {
      if (!attrs.conversation_id) {
        return Room.none
      }
      const conversation = this.primeConversation({ id: attrs.conversation_id })
      GeneralError.assert(conversation, 'No Conversation returned')
      return new Room(conversation, this.primeMemberByPublicId)
    },
  })

  constructor(
    private messagingAPI: MessagingAPI,
    private roomsAPI: RoomsAPI,
    private primeMemberByPublicId: PrimeMemberByPublicId,
    private primeConversation: PrimeConversation,
    private fetchConversation: (id: Id) => Promise<Conversation>,
  ) {
    this.fetchRoom = this.fetchRoom.bind(this)
    this.joinRoom = this.joinRoom.bind(this)
    this.events$ = this.messagingAPI.events$.pipe(
      // Ensures events are processed serially.
      concatMap(this.handleServerEvent.bind(this)),
      filterNonNil(),
      share(),
    )

    this.events$.subscribe()
  }

  /**
   * Fetches a Room by id
   */
  async fetchRoom(roomId: Id) {
    return this.roomsAPI.get(Id.assert(roomId)).then(this.rooms.set)
  }

  primeRoom: PrimeRoom = (props) => {
    if (!props) {
      return Room.none
    }
    if (!props.id) {
      return Room.none
    }

    this.fetchRoom(Id.assert(props.id))
    return this.rooms.set(props)
  }

  async joinRoom(id: Id): Promise<RoomConnectionDescriptor> {
    return this.roomsAPI
      .join(Id.assert(id))
      .then(mapConnectionDescriptorFromDto)
  }

  /**
   * Handles realtime events from the server and maps
   * them to application events.
   *
   * @param event
   * @private
   */
  private async handleServerEvent(event: MessageEventsDTO): Promise<RoomEvent> {
    switch (event.type) {
      case 'ROOM_CREATED':
        return {
          type: 'RoomCreated',
          room: await this.fetchRoom(event.payload.id),
        }
      case 'ROOM_CLOSED':
        return {
          type: 'RoomClosed',
          room: await this.fetchRoom(event.payload.id),
        }
      case 'ROOM_PARTICIPANT_CONNECTED':
        // await this.fetchConversation(event.payload.conversation_id)
        return {
          type: 'RoomParticipantConnected',
          room: await this.fetchRoom(event.payload.id),
          participantUserPublicId: event.payload.participant_user_public_id,
        }
      case 'ROOM_PARTICIPANT_DISCONNECTED':
        return {
          type: 'RoomParticipantDisconnected',
          room: await this.fetchRoom(event.payload.id),
          participantUserPublicId: event.payload.participant_user_public_id,
        }
      case 'ROOM_SESSION_STARTED':
        return {
          type: 'RoomSessionStarted',
          room: await this.fetchRoom(event.payload.id),
          sessionId: event.payload.session_id,
        }
      case 'ROOM_SESSION_ENDED':
        return {
          type: 'RoomSessionEnded',
          room: await this.fetchRoom(event.payload.id),
          sessionId: event.payload.session_id,
        }
      default:
        return {
          type: 'Unknown',
          room: Room.none,
        }
    }
  }
}
